import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Copy, ExternalLink, Eye, FileText, Loader2, ShieldCheck } from 'lucide-react'
import { INCLUDED_LICENSE_DAYS } from '@adthlete/common/utils/dealPricing'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  useExtendLicenseAtRateCardMutation,
  useWithdrawOfferMutation,
  useGetLicenseOptionsQuery,
  type Collaboration,
} from '@/redux/marketerApi'
import { ApplicationActions } from '@/components/applications/ApplicationActions'
import { BrandStepCard } from '@/components/collaboration/BrandStepCard'
import { NilGoLine } from '@/components/collaboration/NilGoStatus'
import { LinkDecision, PlatformIcon, Pill } from './ContentReview'
import { latestSubmission, type Stage } from './stageModel'

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
const day = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

/** What was agreed: the booking, the price and the usage */
export const OfferSummary: React.FC<{ c: Collaboration }> = ({ c }) => {
  const b = (c.offerDetails as any)?.booking
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      <div className="flex justify-between gap-3 sm:block">
        <dt className="text-muted-foreground">What</dt>
        <dd className="font-medium text-foreground">
          {b?.kind === 'rate_card'
            ? `${b.quantity} × ${b.deliverableLabel}${b.theme ? ` · ${b.theme.label}` : ''}`
            : b?.kind === 'custom'
              ? b.description
              : (c.structuredDeliverables ?? [])
                  .map((d) => `${d.platform} ${d.format}`)
                  .join(', ') || 'See the brief'}
        </dd>
      </div>
      <div className="flex justify-between gap-3 sm:block">
        <dt className="text-muted-foreground">Pay</dt>
        <dd className="font-medium text-foreground">
          {c.dealType === 'gifting' ? 'Product + performance bonuses' : money(c.paymentAmount)}
        </dd>
      </div>
      <div className="flex justify-between gap-3 sm:block">
        <dt className="text-muted-foreground">Content due</dt>
        <dd className="font-medium text-foreground">{day(c.contentDueDate)}</dd>
      </div>
      <div className="flex justify-between gap-3 sm:block">
        <dt className="text-muted-foreground">Paid usage</dt>
        <dd className="font-medium text-foreground">
          {INCLUDED_LICENSE_DAYS} days included
          {b?.licenseDays ? ` + ${b.licenseDays} extended` : ''}
        </dd>
      </div>
    </dl>
  )
}

/** Take back an offer the athlete hasn't answered, with an optional note to them */
const WithdrawOffer: React.FC<{ c: Collaboration; first: string }> = ({ c, first }) => {
  const { toast } = useToast()
  const [withdraw, { isLoading }] = useWithdrawOfferMutation()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')

  const confirm = async (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      await withdraw({ id: c.id, message: note.trim() || undefined }).unwrap()
      setOpen(false)
      toast({ title: 'Offer withdrawn', description: `We've let ${first} know.` })
    } catch (err: any) {
      toast({
        title: "Couldn't withdraw the offer",
        description: err?.data?.error ?? 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="text-sm font-semibold text-muted-foreground underline hover:text-foreground"
        >
          Withdraw offer
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Withdraw this offer?</AlertDialogTitle>
          <AlertDialogDescription>
            {first} won't be able to accept it any more, and we'll let them know. It stays on your
            deal list as withdrawn.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
          placeholder={`Add a note for ${first} (optional)`}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Keep the offer</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirm}
            disabled={isLoading}
            className="bg-[#DC2626] text-[#FFFFFF] hover:bg-[#B91C1C]"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Withdraw offer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const AgreementStage: React.FC<{ c: Collaboration; first: string; refresh: () => void }> = ({
  c,
  first,
  refresh,
}) => {
  const accepted = c.timeline.find((t) => (t.metadata as any)?.event === 'offer_accepted')
  const withdrawn = c.timeline.find((t) => (t.metadata as any)?.event === 'offer_withdrawn')
  return (
    <div className="space-y-4">
      {c.status === 'accepted' ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#D9F99D] bg-[#F7FEE7] px-4 py-3">
          <p className="flex items-center gap-2 font-semibold text-foreground">
            <BadgeCheck className="h-5 w-5 text-[#16A34A]" /> Agreement finalized
            {accepted ? (
              <span className="font-normal text-muted-foreground">
                · Accepted {day(accepted.createdAt)}
              </span>
            ) : null}
          </p>
          <Link
            to="offer"
            relative="path"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-semibold text-foreground hover:bg-muted"
          >
            <FileText className="h-4 w-4" /> View agreement
          </Link>
        </div>
      ) : c.status === 'offered' ? (
        <div className="space-y-2">
          <p className="rounded-xl bg-[#EFF6FF] px-4 py-3 text-sm text-[#1E3A8A]">
            Your offer is with {first}. They can accept, counter or pass; you'll be notified.{' '}
            <Link to="offer" relative="path" className="font-semibold underline">
              View or change the offer
            </Link>
          </p>
          <WithdrawOffer c={c} first={first} />
        </div>
      ) : c.status === 'withdrawn' ? (
        <p className="rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">
          You withdrew this offer{withdrawn ? ` on ${day(withdrawn.createdAt)}` : ''}. {first} can
          no longer accept it.
        </p>
      ) : null}
      <OfferSummary c={c} />
      {(c.offerDetails as any)?.message ? (
        <blockquote className="rounded-xl border-l-4 border-[#C0FD72] bg-muted px-4 py-3 text-sm text-foreground">
          “{(c.offerDetails as any).message}”
        </blockquote>
      ) : null}
      {c.status === 'pending_brand' ? (
        <ApplicationActions
          applicationId={c.id}
          athleteName={c.athlete?.name ?? 'the athlete'}
          dealType={c.dealType ?? 'content'}
          ask={c.paymentAmount}
          onDone={refresh}
        />
      ) : null}
    </div>
  )
}

const ComplianceStage: React.FC<{ c: Collaboration; first: string }> = ({ c, first }) =>
  c.compliance?.required ? (
    <div className="space-y-3">
      <NilGoLine compliance={c.compliance} athleteName={c.athlete?.name ?? first} />
      <p className="text-sm text-muted-foreground">
        {first} files the deal with the NIL Go clearinghouse and tells their school. Content can't
        go live until it's cleared; you'll be notified when it is.
      </p>
    </div>
  ) : (
    <p className="text-sm text-muted-foreground">
      Deals under $600 don't need NIL Go clearance, so there's nothing to file for this one.
    </p>
  )

/** The athlete's concept: the story and how the brand fits, approved before filming */
const ConceptStage: React.FC<{
  c: Collaboration
  stage: Stage
  first: string
  refresh: () => void
}> = ({ c, stage, first, refresh }) => {
  const sub = latestSubmission(stage.milestone)
  if (!sub) {
    return (
      <p className="text-sm text-muted-foreground">
        {first} will share their concept here before filming
        {stage.date ? `, by ${day(stage.date)}` : ''}.
      </p>
    )
  }
  let concept = sub.contentDescription ?? ''
  try {
    const parsed = JSON.parse(concept)
    concept =
      (parsed.fields ?? [])
        .map((f: any) => f.value)
        .filter(Boolean)
        .join('\n\n') || concept
  } catch {
    // plain text
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Version {sub.version} · {day(sub.submittedAt)}
      </p>
      <div className="rounded-xl bg-muted px-4 py-3 text-base leading-relaxed text-foreground whitespace-pre-wrap">
        {concept}
      </div>
      {sub.status === 'pending_review' ? (
        <LinkDecision
          collaborationId={c.id}
          milestoneId={stage.milestone!.id}
          first={first}
          onDone={refresh}
        />
      ) : null}
    </div>
  )
}

/** A gifting, appearance or licensing step: the brand's own step, or the athlete's */
const StepStage: React.FC<{
  c: Collaboration
  stage: Stage
  first: string
  refresh: () => void
}> = ({ c, stage, first, refresh }) => {
  if (stage.state === 'needs_you') {
    return (
      <BrandStepCard collaboration={c} athleteName={c.athlete?.name ?? first} onDone={refresh} />
    )
  }
  if (stage.state === 'with_athlete') {
    return (
      <p className="text-sm text-muted-foreground">
        {first}'s step{stage.date ? `, due ${day(stage.date)}` : ''}.{' '}
        {stage.milestone?.description ?? ''} You'll be notified when it's done.
      </p>
    )
  }
  return (
    <p className="text-sm text-muted-foreground">
      {stage.state === 'done'
        ? `Done${stage.date ? ` ${day(stage.date)}` : ''}.`
        : (stage.milestone?.description ?? 'Comes up later in the deal.')}
    </p>
  )
}

const CODE_LABEL: Record<string, { label: string; how: string }> = {
  tiktok: { label: 'Spark Ads code', how: 'Lets you run the post as a TikTok Spark Ad.' },
  instagram: {
    label: 'Partnership ad code',
    how: 'Lets you run the post as a Meta partnership ad.',
  },
  youtube: { label: 'Ad permission', how: 'Athlete confirms you can run the video as an ad.' },
}

/** One-click license extension at the athlete's rate-card price */
const ExtendUsage: React.FC<{ c: Collaboration; first: string }> = ({ c, first }) => {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const { data } = useGetLicenseOptionsQuery(c.id, { skip: !open })
  const [extend, { isLoading }] = useExtendLicenseAtRateCardMutation()
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-sm font-semibold text-[#3F7A0A] hover:underline"
      >
        {open ? 'Hide extension options' : 'Extend usage'}
      </button>
      {open ? (
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {data ? (
            data.options.map((o) => (
              <button
                key={o.days}
                type="button"
                disabled={isLoading}
                onClick={async () => {
                  try {
                    await extend({ collaborationId: c.id, days: o.days }).unwrap()
                    toast({
                      title: `Usage extended ${o.label}`,
                      description: `${money(o.price)}, invoiced separately. ${first} will send updated codes.`,
                    })
                    setOpen(false)
                  } catch (err: any) {
                    toast({
                      title: "Couldn't extend",
                      description: err?.data?.error,
                      variant: 'destructive',
                    })
                  }
                }}
                className="rounded-lg border border-border bg-card px-2 py-2 text-center text-xs hover:bg-muted"
              >
                <span className="block text-muted-foreground">+{o.label}</span>
                <span className="block font-semibold tabular-nums text-foreground">
                  {money(o.price)}
                </span>
              </button>
            ))
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Published posts, how they're doing, and the codes to run them as ads */
const LiveStage: React.FC<{ c: Collaboration; first: string }> = ({ c, first }) => {
  const { toast } = useToast()
  const license = c.milestones.find((m) => m.slug === 'license_content')
  const platforms = license?.metadata?.platforms ?? []
  // Post links are stored as plain URLs or as { url, platform }
  const links = ((c.postLinks ?? []) as unknown[])
    .map((l) => (typeof l === 'string' ? l : ((l as { url?: string })?.url ?? '')))
    .filter(Boolean)
  const usage = c.performance?.usage
  const totalDays =
    (usage?.includedDays ?? 0) +
    (usage?.extensions ?? []).reduce((s: number, e: any) => s + (e.termDays ?? 0), 0)
  const start = usage?.endsAt
    ? new Date(new Date(usage.endsAt).getTime() - totalDays * 86_400_000).toISOString()
    : null
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <section className="rounded-xl border border-border p-4">
        <p className="font-semibold text-foreground">Published</p>
        {links.length ? (
          <ul className="mt-2 space-y-2">
            {links.map((url) => (
              <li key={url}>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm font-medium text-[#2563EB] hover:underline"
                >
                  <PlatformIcon platform={url} className="h-4 w-4" />
                  <span className="truncate">{url.replace(/^https?:\/\/(www\.)?/, '')}</span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            {first} adds the post links once it's live.
          </p>
        )}
        {c.performance?.posted ? (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-foreground">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold tabular-nums">
              {c.performance.views.toLocaleString('en-US')}
            </span>{' '}
            views
          </p>
        ) : null}
      </section>

      <section className="rounded-xl border border-border p-4">
        <p className="flex items-center gap-2 font-semibold text-foreground">
          <ShieldCheck className="h-4 w-4 text-[#16A34A]" /> Paid usage
        </p>
        {usage?.endsAt ? (
          <>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
              {usage.daysLeft} days left
            </p>
            <p className="text-sm text-muted-foreground">
              {day(start)} – {day(usage.endsAt)}
            </p>
          </>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            {INCLUDED_LICENSE_DAYS} days included with the deal. They start when the post goes
            live.
          </p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Part of the agreement, not a new license. The codes below let you use it.
        </p>
        {c.performance?.posted ? (
          <div className="mt-2">
            <ExtendUsage c={c} first={first} />
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-border p-4">
        <p className="font-semibold text-foreground">Ad codes</p>
        {platforms.length ? (
          <ul className="mt-2 space-y-3">
            {platforms.map((p) => {
              const info = CODE_LABEL[p.platform] ?? { label: 'Ad access', how: '' }
              const value = typeof p.submitted_value === 'string' ? p.submitted_value : null
              return (
                <li key={p.platform} className="text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      <PlatformIcon platform={p.platform} className="h-4 w-4" /> {info.label}
                    </span>
                    {p.status === 'completed' ? (
                      <Pill tone="green">Received</Pill>
                    ) : (
                      <Pill tone="amber">Pending</Pill>
                    )}
                  </div>
                  {value ? (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(value)
                          toast({ title: 'Code copied' })
                        } catch {
                          toast({ title: value })
                        }
                      }}
                      className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded-md bg-muted px-2 py-1 font-mono text-xs text-foreground"
                    >
                      <span className="truncate">{value}</span>{' '}
                      <Copy className="h-3.5 w-3.5 shrink-0" />
                    </button>
                  ) : (
                    <p className="text-xs text-muted-foreground">{info.how}</p>
                  )}
                  {p.submitted_at ? (
                    <p className="text-xs text-muted-foreground">Sent {day(p.submitted_at)}</p>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            {c.licensingEnabled
              ? `${first} sends the Spark or partnership codes once the post is live.`
              : 'No ad use on this deal.'}
          </p>
        )}
      </section>
    </div>
  )
}

/** What's inside each stage */
export const StageBody: React.FC<{
  c: Collaboration
  stage: Stage
  first: string
  refresh: () => void
  content: React.ReactNode
}> = ({ c, stage, first, refresh, content }) => {
  switch (stage.kind) {
    case 'agreement':
      return <AgreementStage c={c} first={first} refresh={refresh} />
    case 'compliance':
      return <ComplianceStage c={c} first={first} />
    case 'concept':
      return <ConceptStage c={c} stage={stage} first={first} refresh={refresh} />
    case 'content':
      return <>{content}</>
    case 'live':
      return <LiveStage c={c} first={first} />
    default:
      return <StepStage c={c} stage={stage} first={first} refresh={refresh} />
  }
}
